import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Brain, ChevronRight, CheckCircle2, XCircle,
  Sparkles, Loader2, Trophy,
} from "lucide-react";

/**
 * DailyReviewPage - runs the user through any due SM-2 review cards.
 * Self-contained flow: question → answer → instant feedback → next.
 */
const DailyReviewPage = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState(null);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState({ correct: 0, total: 0 });
  const [nextDueAt, setNextDueAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/study/review/due");
      setCards(res.cards || []);
      setNextDueAt(res.next_due_at);
    } catch {
      setCards([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitAnswer = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const q = cards[idx];
      const res = await api.post("/study/review/answer", {
        question_id: q.id,
        selected_option_id: selected,
      });
      setFeedback(res);
      setStats((s) => ({ correct: s.correct + (res.is_correct ? 1 : 0), total: s.total + 1 }));
    } catch (e) {
      setFeedback({ is_correct: false, explanation: "Could not record this answer." });
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    setSelected(null);
    setFeedback(null);
    setIdx((i) => i + 1);
  };

  if (cards === null) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Empty queue OR finished all
  if (cards.length === 0 || idx >= cards.length) {
    const finished = cards.length > 0 && idx >= cards.length;
    let nextDueLabel = "";
    if (!finished && nextDueAt) {
      try {
        const days = Math.ceil((new Date(nextDueAt) - new Date()) / (1000 * 60 * 60 * 24));
        nextDueLabel = days <= 0 ? "today" : `in ${days} day${days === 1 ? "" : "s"}`;
      } catch { /* silent */ }
    }
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6"
          data-testid="review-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>
        <div className="glass rounded-2xl p-8 border border-border text-center space-y-4" data-testid="review-empty">
          <div className="inline-flex w-14 h-14 rounded-full bg-primary/10 items-center justify-center">
            {finished ? <Trophy className="w-7 h-7 text-primary" /> : <Sparkles className="w-7 h-7 text-primary" />}
          </div>
          <h2 className="text-xl font-bold">{finished ? "Daily review done" : "Nothing due"}</h2>
          {finished ? (
            <p className="text-sm text-muted-foreground">
              You answered <strong>{stats.correct} of {stats.total}</strong> correctly.
              Cards you missed will return tomorrow. The ones you nailed jump further out on the schedule.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              No review cards waiting. {nextDueLabel ? `Next review is due ${nextDueLabel}.` : "Tackle a new topic and your queue will build up automatically."}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full" data-testid="review-go-dashboard">
              Dashboard
            </Button>
            <Button onClick={() => navigate("/learn")} className="rounded-full" data-testid="review-go-learn">
              Study a topic
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const q = cards[idx];
  return (
    <div className="max-w-3xl mx-auto py-8 px-4" data-testid="daily-review-page">
      <button
        onClick={() => navigate("/dashboard")}
        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Exit review
      </button>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-primary font-semibold inline-flex items-center gap-1.5">
            <Brain className="w-3.5 h-3.5" /> Daily Review
          </div>
          <div className="text-xs text-muted-foreground">{idx + 1} of {cards.length}</div>
        </div>
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${((idx + 1) / cards.length) * 100}%` }} />
        </div>
      </div>
      <div className="glass rounded-2xl p-5 md:p-7 border border-border space-y-5">
        {q.clinical_vignette && (
          <div className="text-sm bg-secondary/40 rounded-lg p-3 italic text-muted-foreground">{q.clinical_vignette}</div>
        )}
        <div className="font-semibold text-base md:text-lg">{q.question_text}</div>
        <div className="space-y-2">
          {q.options.map((opt) => {
            let cls = "border-border hover:border-primary/40 hover:bg-secondary/40";
            if (feedback) {
              if (opt.id === feedback.correct_option_id) cls = "border-success bg-success/10";
              else if (opt.id === selected) cls = "border-destructive bg-destructive/10";
              else cls = "border-border opacity-60";
            } else if (selected === opt.id) {
              cls = "border-primary bg-primary/5";
            }
            return (
              <button
                key={opt.id}
                disabled={!!feedback}
                onClick={() => setSelected(opt.id)}
                data-testid={`review-option-${opt.id}`}
                className={`w-full text-left p-3 rounded-xl border-2 transition-all text-sm ${cls}`}
              >
                <span className="inline-block w-6 h-6 rounded-md bg-secondary text-center text-xs font-semibold leading-6 mr-3">
                  {opt.id.toUpperCase()}
                </span>
                {opt.text}
              </button>
            );
          })}
        </div>

        {feedback && (
          <div
            data-testid="review-feedback"
            className={`rounded-xl p-4 border ${feedback.is_correct ? "bg-primary/5 border-primary/30" : "bg-destructive/5 border-destructive/30"}`}
          >
            <div className="flex items-center gap-2 font-semibold mb-2">
              {feedback.is_correct
                ? <><CheckCircle2 className="w-4 h-4 text-primary" /> Correct</>
                : <><XCircle className="w-4 h-4 text-destructive" /> Not quite</>}
            </div>
            {feedback.explanation && (
              <p className="text-sm text-muted-foreground">{feedback.explanation}</p>
            )}
            {feedback.next_interval_days != null && (
              <p className="text-xs text-muted-foreground mt-2">
                {feedback.mastered
                  ? "🎉 This card is now mastered and out of your queue."
                  : `Next review in ${feedback.next_interval_days} day${feedback.next_interval_days === 1 ? "" : "s"}.`}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3 mt-5">
        {!feedback ? (
          <Button
            onClick={submitAnswer}
            disabled={!selected || submitting}
            className="flex-1 rounded-full"
            data-testid="review-submit"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</>) : "Check answer"}
          </Button>
        ) : (
          <Button onClick={next} className="flex-1 rounded-full" data-testid="review-next">
            {idx === cards.length - 1 ? "Finish review" : "Next card"}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default DailyReviewPage;
